import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { ColDef, INumberFilterParams } from 'ag-grid-community'
import { useCallback, useEffect, useState } from 'react'
import MedAgDataGrid from '../../../components/MedAgDataGrid'
import CustomHeader from '../../Radiology/components/RadiologyModel/CustomHeader'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { pageSizeOptions } from '../../../utils/constants'
import ProvenanceView from '../../../components/ProvenanceView'
import {
  AG_FILTER_PARAMS,
  BTN_CREATE,
  CELL_STYLE_ACTION,
  CELL_STYLE_DEFAULT,
  CELL_STYLE_PROVENANCE,
  CELL_STYLE_WRAP,
  EDIT_HOSPITAL_TOOLTIP,
  REFERRED_HOSPITALS_TITLE,
  SEARCH_HOSPITALS_PLACEHOLDER,
} from '../constants'
import HospitalDialog from './HospitalDialog'
import { fetchHospitalListWithPaginations } from '../../../services/referralService'
import MaskedNumber from '../../../components/MaskedNumber'

const styles = () => ({
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
    },
  },
  editIcon: {
    color: '#625e5e',
  },
  pageTitle: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  hospitalSearchField: {
    padding: 'unset',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: '4px !important',
    },
  },
})

const ReferredHospitalList = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [hospitals, setHospitals] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const [openCreate, setOpenCreate] = useState(false)
  const [editHospital, setEditHospital] = useState<any | null>(null)

  const filteredHospitals = hospitals?.filter((hospital) => {
    if (!searchTerm?.trim()) return true
    const searchLower = searchTerm?.toLowerCase().trim()
    return (
      hospital?.name?.toLowerCase().includes(searchLower) ||
      hospital?.location?.toLowerCase().includes(searchLower) ||
      hospital?.city?.toLowerCase().includes(searchLower) ||
      hospital?.type?.toLowerCase().includes(searchLower) ||
      hospital?.phone?.toLowerCase().includes(searchLower) ||
      hospital?.email?.toLowerCase().includes(searchLower) ||
      hospital?.description?.toLowerCase().includes(searchLower)
    )
  })

  const fetchHospitals = useCallback(async () => {
    const list = await fetchHospitalListWithPaginations(page, pageSize)
    if (list?.success) {
      setHospitals(list?.data?.referred_hospital_list ?? [])
      setTotalCount(
        list?.data?.referred_hospital_list_aggregate?.aggregate?.count ?? 0
      )
    } else {
      setHospitals([])
      setTotalCount(0)
    }
  }, [page, pageSize])

  useEffect(() => {
    fetchHospitals()
  }, [fetchHospitals, page, pageSize])

  const closeCreate = () => {
    setOpenCreate(false)
    fetchHospitals()
  }

  const closeEdit = () => {
    setEditHospital(null)
    fetchHospitals()
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'SL No',
      field: 'slNo',
      minWidth: 80,
      maxWidth: 90,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      cellRenderer: (params: any) => params?.node?.rowIndex + 1,
    },
    {
      headerName: 'Hospital Name',
      field: 'name',
      flex: 1,
      minWidth: 180,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.2,
      },
      autoHeight: true,
      wrapText: true,
      cellRenderer: (params: any) => params?.data?.name ?? '',
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Type',
      field: 'type',
      flex: 1,
      minWidth: 180,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_DEFAULT,
      cellRenderer: (params: any) => params?.data?.type ?? '',
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 2,
      minWidth: 260,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      wrapText: true,
      autoHeight: true,
      cellStyle: CELL_STYLE_WRAP,
      cellRenderer: (params: any) => params?.data?.description ?? '',
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Location',
      field: 'location',
      flex: 1,
      minWidth: 140,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_DEFAULT,
      cellRenderer: (params: any) => params?.data?.location ?? '',
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Address',
      field: 'address',
      flex: 1.5,
      minWidth: 260,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_DEFAULT,
      cellRenderer: (params: any) =>
        [
          params?.data?.street,
          params?.data?.city,
          params?.data?.state,
          params?.data?.pincode,
        ]
          ?.filter(Boolean)
          ?.join(', '),
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Phone',
      field: 'phone',
      minWidth: 130,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_DEFAULT,
      cellRenderer: (params: any) =>
        params?.data?.phone ? (
          <MaskedNumber mobile={params?.data?.phone} />
        ) : (
          ''
        ),
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_DEFAULT,
      cellRenderer: (params: any) => params?.data?.email ?? '',
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      minWidth: 160,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      wrapText: true,
      autoHeight: true,
      cellStyle: CELL_STYLE_PROVENANCE,
      cellRenderer: (params: any) => (
        <ProvenanceView
          mode='CREATE'
          name={params?.data?.created_by ?? 'Unknown'}
          date={params?.data?.created_on ?? ''}
        />
      ),
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Updated By',
      field: 'updatedBy',
      minWidth: 160,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      wrapText: true,
      autoHeight: true,
      cellStyle: CELL_STYLE_PROVENANCE,
      cellRenderer: (params: any) => (
        <ProvenanceView
          mode='UPDATE'
          name={params?.data?.updated_by ?? ''}
          date={params?.data?.updated_on ?? ''}
        />
      ),
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: 'Action',
      field: 'actions',
      minWidth: 90,
      maxWidth: 100,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_ACTION,
      cellRenderer: (params: any) => (
        <Tooltip title={EDIT_HOSPITAL_TOOLTIP}>
          <IconButton
            size='small'
            onClick={() => setEditHospital(params?.data)}
          >
            <EditIcon fontSize='small' className={classes?.editIcon} />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <>
      <Box
        height='100%'
        width='100%'
        padding={2}
        display='flex'
        flexDirection='column'
        gap={0.5}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box className={classes?.pageTitle}>{REFERRED_HOSPITALS_TITLE}</Box>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <TextField
              className={classes?.hospitalSearchField}
              placeholder={SEARCH_HOSPITALS_PLACEHOLDER}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button
              variant='contained'
              size='small'
              color='primary'
              onClick={() => setOpenCreate(true)}
            >
              {BTN_CREATE}
            </Button>
          </Box>
        </Box>
        <MedAgDataGrid
          tableHeight='100%'
          rowData={filteredHospitals}
          columnDefs={columnDefs}
          pagination={true}
          pageSizeOptions={pageSizeOptions}
          exportIcon={false}
          searchField={false}
          headerHeight={30}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRows={totalCount}
          localSearch={false}
          enableCellTextSelection={true}
          loading={false}
        />
      </Box>
      <HospitalDialog open={openCreate} onClose={closeCreate} />
      <HospitalDialog
        open={editHospital !== null}
        onClose={closeEdit}
        hospital={editHospital}
      />
    </>
  )
}

export default ReferredHospitalList
